import { useEffect, useMemo, useState } from "react";
import { ProductCardLoader } from "./btn-drawer";
import { addEventAttributes, cloneNode } from "../ui-script/utils";
import { TrackWrapper } from "./track/wrapper";
import { handleEvent } from "./track/util";
import React from "react";
import { Product } from "../types";
import { createElementFromString } from "@/datastore/utils";
import { ComplementaryStyleGrid } from "./sub-components/complementary-style-grid";
import { HeliumEventInfo } from "@/utils/tracking";

export interface RecommendationProps {
  complementartLst: Record<string, Category>;
  complementaryStyles: Record<
    string,
    {
      position: string;
      subcategory: string;
    }[]
  >;
  loading: boolean;
  similarLst: any;
  type?: "recommended" | "complete-the-look" | "low-ticket-size";
  currentProductHandle?: string;
  showComplementaryGrid?: boolean;
  hideTypeSelectionTab?: boolean;
  showAddToCartButton?: boolean;
  onAddToCart?: (handle: string) => void;
  defaultSelected?: boolean;
  currentProductPosition: string;
  lowTicketSize?: any;
  showInCart?: boolean;
}
export const Recommendation = (props: RecommendationProps) => {
  return (
    <TrackWrapper
      event={{
        name: "recommendation",
        properties: {
          category: props.type,
          handle: props.currentProductHandle,
        },
      }}
    >
      <RecommendationComponent {...props} />
    </TrackWrapper>
  );
};

export function RecommendationComponent({
  complementartLst,
  loading,
  similarLst,
  type = "complete-the-look",
  currentProductHandle,
  showComplementaryGrid = true,
  hideTypeSelectionTab = false,
  showAddToCartButton = false,
  onAddToCart,
  defaultSelected,
  complementaryStyles,
  currentProductPosition,
  lowTicketSize,
  showInCart = false,
}: RecommendationProps) {
  function getFirstProduct(category: string): {
    subcategory: string;
    image: string;
  } {
    if (!category) return { subcategory: "", image: "" };

    for (const [key, subcategory] of Object.entries(
      complementartLst[category],
    )) {
      if (subcategory.length > 0) {
        return {
          subcategory: key ?? "",
          image: subcategory[0]?.image,
        };
      }
    }
    return { subcategory: "complementary", image: "" };
  }

  const [selectedCategory, setSelectedCategory] = useState<Category | null>(
    defaultSelected ? Object.keys(complementartLst)[0] : null,
  );

  const [selectedLowTicketSizeCategory, setSelectedLowTicketSizeCategory] =
    useState<string | null>(Object.keys(lowTicketSize ?? {})[0]);

  useEffect(() => {
    if (selectedLowTicketSizeCategory || !lowTicketSize) return;

    setSelectedLowTicketSizeCategory(Object.keys(lowTicketSize ?? {})[0]);
  }, [lowTicketSize]);

  const [selectedSubCategory, setSelectedSubCategory] = useState(
    getFirstProduct(selectedCategory)?.subcategory,
  );

  const [activeTab, setActiveTab] = useState<
    "recommended" | "complete-the-look"
  >(type);

  const [lowTicketSizeProducts, setLowTicketSizeProducts] = useState<string[]>(
    [],
  );

  const [similarProducts, setSimilarProducts] = useState<string[]>([]);
  const [complementaryProductsHtml, setComplementaryProductsHtml] = useState<
    string[]
  >([]);

  useEffect(() => {
    const fetchRenderedProducts = async () => {
      const productHtmls = await Promise.all(
        similarLst?.map((item) => renderCloneNode(item, { type: "similar" })),
      );
      setSimilarProducts(productHtmls.map((html) => html.__html));
    };

    fetchRenderedProducts();
  }, [similarLst]);

  useEffect(() => {
    if (!lowTicketSize) return;

    const lowTicketSizeCategoryItems =
      lowTicketSize[selectedLowTicketSizeCategory ?? ""];

    const firstAttribute = Object.keys(lowTicketSizeCategoryItems ?? {})[0];

    const fetchRenderedProducts = async () => {
      const productHtmls = await Promise.all(
        lowTicketSizeCategoryItems?.[firstAttribute]?.map((item) =>
          renderCloneNode(item, {
            type: "low-ticket",
            category: selectedLowTicketSizeCategory,
          }),
        ),
      );
      setLowTicketSizeProducts(productHtmls.map((html) => html.__html));
    };
    fetchRenderedProducts();
  }, [lowTicketSize, selectedLowTicketSizeCategory]);

  useEffect(() => {
    if (!selectedCategory) return;
    setSelectedSubCategory(getFirstProduct(selectedCategory)?.subcategory);
  }, [selectedCategory]);

  useEffect(() => {
    const fetchComplementaryProductsHtml = async () => {
      if (!selectedCategory || !selectedSubCategory) return;

      const productsHtml = await Promise.all(
        complementartLst[selectedCategory][selectedSubCategory].map((item) =>
          renderCloneNode(item, { type: "complementary" }).then(
            (result) => result.__html,
          ),
        ),
      );

      setComplementaryProductsHtml(productsHtml);
    };

    fetchComplementaryProductsHtml();
  }, [selectedCategory, selectedSubCategory, complementartLst]);

  const renderSubCategories = () => {
    const subCategories = Object.keys(
      complementartLst[String(selectedCategory) ?? ""] ?? {},
    );

    if (subCategories.length < 2) return null;
    if (!selectedCategory) return null;

    return (
      <ul className="hp-no-scrollbar hp-hp-flex hp-hp-overflow-y-scroll hp-hp-border-b">
        {subCategories.map((subcategory) =>
          complementartLst[selectedCategory][subcategory].length > 0 ? (
            <button
              key={subcategory}
              className={`hp-mr-1.5 hp-px-4 hp-py-2 hp-text-sm hp-font-semibold hp-uppercase ${
                selectedSubCategory === subcategory
                  ? "hp-border-b-2 hp-border-[#181818] hp-text-[#181818]"
                  : "hp-border-b hp-text-[#A7A7A7]"
              }`}
              onClick={() => setSelectedSubCategory(subcategory)}
              {...addEventAttributes({
                eventName: "inner_tab_click",
                componentType: "complementary",
                eventData: {
                  look: subcategory,
                },
              })}
            >
              {subcategory}
            </button>
          ) : null,
        )}
      </ul>
    );
  };

  const renderCloneNode = async (
    item: Product,
    evData: {
      type: "complementary" | "similar" | "low-ticket";
      category: string;
    },
  ) => {
    const templateNode = window.helium.productCard
      ? createElementFromString(window.helium.productCard)
      : window.helium.datastore.memory.html.product;
    const handle =
      window.helium.productCardHandle ||
      window.helium.datastore.memory.html.productHandle ||
      window.location.pathname.split("/").at(-1);

    if (!templateNode) return null;

    let existingProduct = (
      await window.helium.datastore.getProduct({
        id: handle ?? "",
      })
    )?.details;

    if (window.location.pathname.includes("product") && !existingProduct) {
      existingProduct = window.helium.datastore.memory.product["test"]?.details;
    }

    const node = cloneNode(templateNode, item, existingProduct, false, {
      componentType: evData.type as HeliumEventInfo["componentType"],
      eventData: {
        category: evData.category,
      },
    });
    node.className +=
      " !hp-w-[var(--h-card-width,200px)] !hp-min-w-[var(--h-card-width,200px)] !hp-max-w-[var(--h-card-width,200px)]";

    return { __html: node.outerHTML };
  };

  const renderCategories = () => {
    if (!selectedCategory && !selectedSubCategory) return null;

    return (
      <ul className="no-scrollbar hp-flex hp-overflow-x-scroll hp-border-b">
        {Object.keys(complementartLst ?? {}).map((item) => (
          <button
            key={item}
            className={`hp-mr-1.5 hp-whitespace-nowrap hp-px-4 hp-py-2 hp-text-xl hp-font-semibold hp-uppercase ${
              selectedCategory === item
                ? "hp-border-b-2 hp-border-[#181818] hp-text-[#181818]"
                : "hp-border-b hp-text-[#A7A7A7]"
            }`}
            onClick={() => setSelectedCategory(item)}
            {...addEventAttributes({
              eventName: "inner_tab_click",
              componentType: "complementary",
              eventData: {
                look: item,
              },
            })}
          >
            {item}
          </button>
        ))}
      </ul>
    );
  };

  const hasComplementary = useMemo(() => {
    return Object.keys(complementaryStyles ?? {}).length > 0;
  }, [complementaryStyles]);

  return (
    <>
      {!hideTypeSelectionTab && (
        <div className="hp-flex hp-border-b swimlane hp-px-4">
          <button
            className={`hp-py-2 hp-text-xl hp-mt-4 hp-font-semibold hp-uppercase ${
              activeTab === "recommended"
                ? "hp-border-b-2 hp-border-[#181818] hp-text-[#181818]"
                : "hp-border-b hp-text-[#A7A7A7]"
            }`}
            onClick={() => setActiveTab("recommended")}
            {...addEventAttributes({
              eventName: "tab_click",
              componentType: "similar",
              eventData: {
                productId: currentProductHandle,
              },
            })}
          >
            Similar Finds
          </button>

          {!!lowTicketSize && (
            <button
              className={`hp-py-2 hp-text-xl hp-mt-4 hp-font-semibold hp-uppercase ${
                activeTab === "low-ticket-size"
                  ? "hp-border-b-2 hp-border-[#181818] hp-text-[#181818]"
                  : "hp-border-b hp-text-[#A7A7A7]"
              }`}
              {...addEventAttributes({
                eventName: "tab_click",
                componentType: "low_ticket",
                eventData: {
                  productId: currentProductHandle,
                },
              })}
              onClick={() => setActiveTab("low-ticket-size")}
            >
              Steal deals
            </button>
          )}

          {hasComplementary &&
            !window.heliumConfig?.features?.better_together?.disabled && (
              <button
                className={`hp-py-2 hp-text-xl hp-mt-4 hp-font-semibold hp-uppercase ${
                  activeTab === "complete-the-look"
                    ? "hp-border-b-2 hp-border-[#181818] hp-text-[#181818]"
                    : "hp-border-b hp-text-[#A7A7A7]"
                }`}
                {...addEventAttributes({
                  eventName: "tab_click",
                  componentType: "complementary",
                  eventData: {
                    productId: currentProductHandle,
                  },
                })}
                onClick={() => {
                  handleEvent({
                    name: "complete-the-look",
                    properties: {
                      handle: currentProductHandle,
                      category: "complete-the-look",
                    },
                  });
                  setActiveTab("complete-the-look");
                }}
              >
                Better together
              </button>
            )}
        </div>
      )}

      {loading ? (
        <ProductCardLoader />
      ) : (
        <div className="hp-px-0 hp-py-2">
          {activeTab === "complete-the-look" ? (
            <>
              {!selectedCategory && showComplementaryGrid ? (
                <ComplementaryStyleGrid
                  complementaryStyles={complementaryStyles}
                  currentProductPosition={currentProductPosition}
                  currentProductHandle={currentProductHandle!}
                />
              ) : (
                <>
                  <div className="hp-space-y-2">
                    {renderCategories()}
                    {renderSubCategories()}
                  </div>
                  <ul
                    className={`swimlane2 no-scrollbar hp-mt-4 hp-flex hp-w-full hp-pl-2 hp-gap-[24px] ${window.helium.productListClasses}`}
                  >
                    {complementaryProductsHtml.length > 0 ? (
                      complementaryProductsHtml.map((html, i) => (
                        <ClickListener
                          event={{
                            name: "product-clicked-complementary",
                            properties: {
                              current: window.location.pathname
                                .split("/")
                                .at(-1),
                              cardProduct: html.match(
                                /data-helium-product-handle="([^"]+)"/,
                              )?.[1],
                            },
                          }}
                        >
                          <div
                            key={i}
                            dangerouslySetInnerHTML={{
                              __html:
                                html +
                                (showAddToCartButton && !showInCart
                                  ? `<button onclick="window.heliumATC('${
                                      html.match(
                                        /data-helium-product-handle="([^"]+)"/,
                                      )?.[1]
                                    }')" class='hp-w-full hp-bg-black hp-text-white hp-px-2 hp-py-1 hp-rounded-sm'>Add to cart</button>`
                                  : ""),
                            }}
                            className="hp-w-[200px]"
                          />
                        </ClickListener>
                      ))
                    ) : (
                      <h5 className="hp-flex hp-min-h-[200px] hp-w-full hp-place-items-center hp-justify-center hp-text-sm hp-font-semibold hp-uppercase hp-text-[#A7A7A7]">
                        No products found
                      </h5>
                    )}
                  </ul>
                </>
              )}
            </>
          ) : activeTab === "recommended" ? (
            <>
              <ul
                className={`swimlane2 no-scrollbar hp-mt-4 hp-flex hp-w-full hp-pl-2 hp-gap-[24px] ${window.helium.productListClasses}`}
              >
                {similarProducts.length > 0 ? (
                  similarProducts.map((html, i) => (
                    <ClickListener
                      event={{
                        name: "product-clicked-similar",
                        properties: {
                          current: window.location.pathname.split("/").at(-1),
                          cardProduct: html.match(
                            /data-helium-product-handle="([^"]+)"/,
                          )?.[1],
                        },
                      }}
                    >
                      <div key={i} dangerouslySetInnerHTML={{ __html: html }} />
                    </ClickListener>
                  ))
                ) : (
                  <h5 className="hp-flex hp-min-h-[200px] hp-w-full hp-place-items-center hp-justify-center hp-text-sm hp-font-semibold hp-uppercase hp-text-[#A7A7A7]">
                    No products found
                  </h5>
                )}
              </ul>
            </>
          ) : (
            <>
              {" "}
              <div className="hp-space-y-2">
                <ul className="no-scrollbar hp-flex hp-overflow-x-scroll hp-border-b">
                  {Object.keys(lowTicketSize ?? {})
                    .filter((e) => {
                      if (
                        !(
                          window.heliumConfig?.features?.steal_deals
                            ?.blacklistTabs ?? []
                        )?.includes(e.toLowerCase())
                      ) {
                        return true;
                      }
                      return false;
                    })
                    .map((item) => (
                      <button
                        key={item}
                        className={`hp-mr-1.5 hp-whitespace-nowrap hp-px-4 hp-py-2 hp-text-xl hp-font-semibold hp-uppercase ${
                          selectedLowTicketSizeCategory === item
                            ? "hp-border-b-2 hp-border-[#181818] hp-text-[#181818]"
                            : "hp-border-b hp-text-[#A7A7A7]"
                        }`}
                        onClick={(e) => {
                          e.preventDefault();
                          setSelectedLowTicketSizeCategory(item);
                        }}
                        {...addEventAttributes({
                          eventName: "inner_tab_click",
                          componentType: "low_ticket",
                          eventData: {
                            category: item,
                          },
                        })}
                      >
                        {item}
                      </button>
                    ))}
                </ul>
              </div>
              <ul
                className={`swimlane2 no-scrollbar hp-mt-4 hp-flex hp-w-full hp-pl-2 hp-gap-[24px] ${window.helium.productListClasses}`}
              >
                {lowTicketSizeProducts.length > 0 ? (
                  lowTicketSizeProducts.map((html, i) => (
                    <ClickListener
                      event={{
                        name: "product-clicked-low-ticket-size",
                        properties: {
                          current: window.location.pathname.split("/").at(-1),
                          cardProduct: html.match(
                            /data-helium-product-handle="([^"]+)"/,
                          )?.[1],
                        },
                      }}
                    >
                      <div key={i} dangerouslySetInnerHTML={{ __html: html }} />
                    </ClickListener>
                  ))
                ) : (
                  <h5 className="hp-flex hp-min-h-[200px] hp-w-full hp-place-items-center hp-justify-center hp-text-sm hp-font-semibold hp-uppercase hp-text-[#A7A7A7]">
                    No products found
                  </h5>
                )}
              </ul>
            </>
          )}
        </div>
      )}
    </>
  );
}

function ClickListener({
  children,
  event,
}: {
  children: React.ReactNode;
  event: any;
}) {
  const childrenHavingOnOverFunction = React.Children.map(children, (child) => {
    return React.cloneElement(child, {
      onClick: () => {
        window?.trackHeliumEvent?.(event);
      },
    });
  });

  return <div onClick={() => {}}>{childrenHavingOnOverFunction}</div>;
}
